// Pure planning for "copy bookings" — one→one (CopyBookingsDialog) and one→many
// (MultiCopyBookingsDialog) share it so the rules can't drift. No network, no JSX.
//
// A "block" is the BookingsTab's normalised booking:
//   { id, productId, productName, productType, campus, actualCharge, start, end, raw }

import { typeKey } from "../../shared utils/bookingsGrid";

const maxStr = (a, b) => (a > b ? a : b);
const minStr = (a, b) => (a < b ? a : b);
const normCampus = (v) => String(v ?? "").trim().toLowerCase();
const overlaps = (aS, aE, bS, bE) => Boolean(aS && aE && bS && bE) && aS <= bE && bS <= aE;
// Strict: sharing only a boundary day doesn't count (residential weeks abut).
const overlapsInside = (aS, aE, bS, bE) => Boolean(aS && aE && bS && bE) && aS < bE && bS < aE;

export const BOOKING_STATUS_NAMES = ["PROVISIONAL", "CONFIRMED", "PAID"];

// The student booking feed sends bookingStatus as an ordinal; the write APIs take
// the enum name. Anything unrecognised falls back to PROVISIONAL.
export function bookingStatusName(v) {
  if (typeof v === "number") return BOOKING_STATUS_NAMES[v] || "PROVISIONAL";
  const s = String(v ?? "").trim().toUpperCase();
  if (BOOKING_STATUS_NAMES.includes(s)) return s;
  if (/^\d+$/.test(s)) return BOOKING_STATUS_NAMES[Number(s)] || "PROVISIONAL";
  return "PROVISIONAL";
}

// "In-group" = overlaps the group window and (when both are known) is on the group
// campus. Bookings outside that are never copied and never removed.
export function inGroupScope(b, { groupStart, groupEnd, groupCampus }) {
  if (!overlaps(b.start, b.end, groupStart, groupEnd)) return false;
  if (b.campus && groupCampus) return normCampus(b.campus) === normCampus(groupCampus);
  return true;
}

// The destination's stay span, from its stay/NSR records — bookings are clamped to it.
export function rangeOfRecords(records) {
  const recs = Array.isArray(records) ? records : [];
  const a = recs.map((r) => r.start).filter(Boolean).sort()[0];
  const b = recs.map((r) => r.end).filter(Boolean).sort().slice(-1)[0];
  return a && b ? { start: a, end: b } : null;
}

// Plan the writes that take `dst` towards `src`.
//   mode "match": add, adjust AND remove, so the destination ends up identical (the
//                 original one→one behaviour).
//   mode "apply": add and adjust only — the destination keeps anything extra, so a
//                 student who diverges (e.g. paid-for horse riding) isn't flattened.
// Returns { toAdd, toAdjust, toRemove, skipped } where skipped are source bookings
// that don't overlap the destination's stay at all.
export function planCopy({ src, dst, destRange, mode = "match" }) {
  const clamp = (b) => {
    let cs = b.start;
    let ce = b.end;
    if (destRange) {
      cs = maxStr(cs, destRange.start);
      ce = minStr(ce, destRange.end);
    }
    return { customStart: cs, customEnd: ce, ok: Boolean(cs && ce) && cs <= ce };
  };

  const dstByProduct = new Map();
  dst.forEach((b) => {
    if (!dstByProduct.has(String(b.productId))) dstByProduct.set(String(b.productId), b);
  });
  const srcProductIds = new Set(src.map((b) => String(b.productId)));

  const toAdd = [];
  const toAdjust = [];
  const skipped = [];
  src.forEach((b) => {
    const match = dstByProduct.get(String(b.productId));
    const c = clamp(b);
    if (!c.ok) {
      skipped.push({ src: b, reason: "outside the destination's stay" });
      return;
    }
    if (!match) {
      toAdd.push({ src: b, customStart: c.customStart, customEnd: c.customEnd });
    } else {
      const differs =
        match.start !== c.customStart ||
        match.end !== c.customEnd ||
        Number(match.actualCharge) !== Number(b.actualCharge);
      if (differs) toAdjust.push({ src: b, dest: match, customStart: c.customStart, customEnd: c.customEnd });
    }
  });
  const toRemove = mode === "match" ? dst.filter((b) => !srcProductIds.has(String(b.productId))) : [];

  return { toAdd, toAdjust, toRemove, skipped };
}

// What the destination's in-scope bookings look like once the plan is applied.
export function endState({ dst, plan }) {
  const removed = new Set(plan.toRemove.map((b) => b.id));
  const adjusted = new Map(plan.toAdjust.map((a) => [a.dest.id, a]));
  const kept = dst
    .filter((b) => !removed.has(b.id))
    .map((b) => {
      const a = adjusted.get(b.id);
      return a ? { ...b, start: a.customStart, end: a.customEnd, actualCharge: a.src.actualCharge } : b;
    });
  const added = plan.toAdd.map((a) => ({
    ...a.src,
    id: null,
    start: a.customStart,
    end: a.customEnd,
  }));
  return [...kept, ...added];
}

const label = (b) => `${b.productName || "Product"} (${b.start} → ${b.end})`;

// The rules the product picker enforces one booking at a time, run over a whole
// programme — this is what makes a bulk copy safe to do without eyeballing every
// grid. Returns [] when clean, else human-readable reasons.
//   blocks: the end state; stayRecords: the destination's stays [{ residential }]
export function findConflicts(blocks, stayRecords = []) {
  const out = [];
  const seen = new Map();
  blocks.forEach((b) => {
    const k = String(b.productId);
    if (seen.has(k)) out.push(`Booked twice: ${b.productName || `product ${k}`}`);
    else seen.set(k, b);
  });

  const isRes = (b) => typeKey(b.productType) === "RESIDENTIAL";
  const isWeekend = (b) => typeKey(b.productType) === "WEEKEND";

  const res = blocks.filter(isRes);
  for (let i = 0; i < res.length; i += 1) {
    for (let j = i + 1; j < res.length; j += 1) {
      const a = res[i];
      const b = res[j];
      if (String(a.productId) === String(b.productId)) continue;
      if (overlapsInside(a.start, a.end, b.start, b.end)) {
        out.push(`Residential clash: ${label(a)} vs ${label(b)}`);
      }
    }
  }

  const wk = blocks.filter(isWeekend);
  for (let i = 0; i < wk.length; i += 1) {
    for (let j = i + 1; j < wk.length; j += 1) {
      const a = wk[i];
      const b = wk[j];
      if (String(a.productId) === String(b.productId)) continue;
      if (overlaps(a.start, a.end, b.start, b.end)) {
        out.push(`Weekend clash: ${label(a)} vs ${label(b)}`);
      }
    }
  }

  // A residential product on a non-residential (day) stay. Only when the feed says
  // so explicitly — an unknown flag isn't a conflict.
  const recs = Array.isArray(stayRecords) ? stayRecords : [];
  res.forEach((b) => {
    const covering = recs.filter((r) => r.start && r.end && overlaps(b.start, b.end, r.start, r.end));
    if (covering.length && covering.every((r) => r.residential === false)) {
      out.push(`Residential product on a non-residential stay: ${label(b)}`);
    }
  });

  return Array.from(new Set(out));
}

// Full per-destination plan for the multi-copy: plan + end state + conflicts.
export function planForDestination({ srcBlocks, dstBlocks, destRecords, scope, mode }) {
  const src = srcBlocks.filter((b) => inGroupScope(b, scope));
  const dst = dstBlocks.filter((b) => inGroupScope(b, scope));
  const destRange = rangeOfRecords(destRecords);
  const plan = planCopy({ src, dst, destRange, mode });
  const after = endState({ dst, plan });
  const conflicts = destRange ? findConflicts(after, destRecords) : ["No stay — nothing to copy onto"];
  const changes = plan.toAdd.length + plan.toAdjust.length + plan.toRemove.length;
  return { plan, after, conflicts, changes, destRange };
}
